import React, { useState, useEffect, useRef } from 'react';
import { FONT } from '../constants';
import { C } from '../styles';

interface Props {
  value: string;
  onSave: (text: string, applyAll: boolean) => void;
}

export default function MemoPopover({ value, onSave }: Props) {
  const [open, setOpen] = useState<boolean>(false);
  const [text, setText] = useState<string>(value || '');
  const [applyAll, setApplyAll] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const save = (): void => {
    onSave(text, applyAll);
    setOpen(false);
  };
  const clear = (): void => setText('');

  const hasMemo = Boolean(value);
  const remaining = 250 - text.length;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onClick={() => { setOpen((o) => !o); setText(value || ''); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28,
          border: '1.5px solid ' + (hasMemo ? C.brand : C.line),
          borderRadius: 6,
          background: hasMemo ? C.brandBg : '#fff',
          cursor: 'pointer',
          color: hasMemo ? C.brandDark : C.muted,
          fontSize: 16, fontWeight: hasMemo ? 700 : 400,
        }}
      >+</div>
      {open && (
        <div style={{
          position: 'absolute', top: 32, right: 0,
          background: '#fff', border: '1px solid ' + C.line,
          borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
          width: 320, zIndex: 30, padding: '16px 18px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Memo</div>
            <a onClick={clear} style={{ fontSize: 12, color: C.brandDark, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
              Clear
            </a>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 250))}
            placeholder="Write a memo for this employee..."
            maxLength={250}
            style={{
              width: '100%', boxSizing: 'border-box', minHeight: 80,
              padding: '10px 12px',
              border: '1px solid ' + C.line, borderRadius: 8,
              fontSize: 13, color: C.ink, fontFamily: FONT, resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4, fontSize: 11, color: C.muted }}>
            {remaining} characters left
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={applyAll}
              onChange={(e) => setApplyAll(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: C.brand }}
            />
            <span style={{ fontSize: 13, color: C.ink }}>Apply to all employees</span>
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button
              onClick={() => setOpen(false)}
              style={{
                flex: 1, background: 'transparent',
                border: '1px solid ' + C.line, color: C.ink,
                fontSize: 13, fontWeight: 600,
                padding: '8px 14px', borderRadius: 8,
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Cancel
            </button>
            <button
              onClick={save}
              style={{
                flex: 1, background: C.brand, color: 'white',
                fontSize: 13, fontWeight: 600,
                padding: '8px 14px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}